import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  InputBase,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
  Snackbar,
} from "@mui/material";
import { Search, Category, TableRestaurant } from "@mui/icons-material";
import { useSession } from "../context/SessionContext";
import useMenu from "../hooks/useMenu";
import useDebouncedValue from "../hooks/useDebouncedValue";
import ProductCard from "../components/ProductCard";
import ProductDetailDialog from "../components/ProductDetailDialog";
import CategoryPickerModal from "../components/CategoryPickerModal";
import BottomCartBar from "../components/BottomCartBar";
import AutoLoadMore from "../components/AutoLoadMore";

export default function Home() {
  const { store, tableKey, table } = useSession();

  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput);
  const [category, setCategory] = useState("all");
  const { loading, loadingMore, error, categories, products, hasMore, loadMoreProducts } = useMenu({ search, category });

  const [activeProductId, setActiveProductId] = useState(null);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [addedSnackOpen, setAddedSnackOpen] = useState(false);

  const categoryLabel = category === "all" ? "All Categories" : categories.find((c) => c.id === category)?.name || "Category";

  return (
    <Box>
      <AppBar position="sticky" color="default" elevation={0} sx={{ bgcolor: "#fff", borderBottom: "1px solid", borderColor: "divider" }}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ flex: 1 }} noWrap>
            {store?.name || "Menu"}
          </Typography>
          {tableKey && (
            <Chip icon={<TableRestaurant fontSize="small" />} label={`Table ${table?.name || tableKey}`} size="small" color="primary" variant="outlined" />
          )}
        </Toolbar>
        <Box sx={{ px: 2, pb: 1.5, display: "flex", gap: 1 }}>
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", bgcolor: "grey.100", borderRadius: 3, px: 1.5 }}>
            <Search fontSize="small" color="disabled" />
            <InputBase
              placeholder="Search menu"
              fullWidth
              sx={{ ml: 1, py: 0.8 }}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </Box>
          <Tooltip title={categoryLabel}>
            <IconButton
              onClick={() => setCategoryModalOpen(true)}
              sx={{
                border: "1px solid",
                borderColor: category !== "all" ? "primary.main" : "divider",
                borderRadius: 3,
                bgcolor: category !== "all" ? "primary.main" : "transparent",
                color: category !== "all" ? "primary.contrastText" : "text.primary",
                "&:hover": { bgcolor: category !== "all" ? "primary.dark" : "action.hover" },
              }}
            >
              <Category fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </AppBar>

      <Box sx={{ p: 2, pb: 12 }}>
        {!tableKey && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No table found. Please scan the QR code on your table to order.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : products.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
            No products found.
          </Typography>
        ) : (
          <>
            {products.map((p) => (
              <ProductCard key={p.id} product={p} onPress={(prod) => setActiveProductId(prod.id)} />
            ))}
            <AutoLoadMore hasMore={hasMore} loadingMore={loadingMore} onLoadMore={loadMoreProducts} />
          </>
        )}
      </Box>

      <ProductDetailDialog
        productId={activeProductId}
        onClose={() => setActiveProductId(null)}
        onAdded={() => setAddedSnackOpen(true)}
      />
      <CategoryPickerModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        categories={categories}
        value={category}
        onSelect={setCategory}
      />
      <BottomCartBar />

      <Snackbar
        open={addedSnackOpen}
        autoHideDuration={1500}
        onClose={() => setAddedSnackOpen(false)}
        message="Added to your order"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
